import { useState, useCallback } from 'react';
import { createDragPayload, isDropOnElement } from '../utils/stackUtils';
import { persistQwantum } from 'qwanyx';

export function useStackDragDrop(stackOps, refreshAfterDrop, debug = false) {
    const [isHovering, setIsHovering] = useState(false);
    const [isHoveringOverIcon, setIsHoveringOverIcon] = useState(false);

    const handleDragStart = useCallback((e, itemId, isFolder, displayText, localItemData, isRoot) => {
        e.stopPropagation();

        if (isRoot) {
            e.preventDefault();
            return;
        }

        const payload = createDragPayload(itemId, isFolder, displayText, localItemData);
        e.dataTransfer.setData('text/plain', JSON.stringify(payload));

        e.currentTarget.classList.add('dragging');
        e.dataTransfer.effectAllowed = 'move';

        // Create drag image
        const dragImage = document.createElement('div');
        dragImage.className = 'drag-ghost';
        dragImage.innerHTML = `
            <i class="fas ${isFolder ? 'fa-folder' : 'fa-file'} small"></i>
            <span>${displayText}</span>
        `;
        document.body.appendChild(dragImage);
        e.dataTransfer.setDragImage(dragImage, 15, 15);

        setTimeout(() => {
            if (document.body.contains(dragImage)) {
                document.body.removeChild(dragImage);
            }
        }, 0);
    }, []);

    const handleDragEnd = useCallback((e) => {
        e.stopPropagation();
        e.currentTarget.classList.remove('dragging');
    }, []);

    const handleDragEnter = useCallback((e, iconRef, isRoot) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (!isRoot) {
            setIsHovering(true);
            if (iconRef?.current && iconRef.current.contains(e.target)) {
                setIsHoveringOverIcon(true);
            }
        }
    }, []);

    const handleDragOver = useCallback((e, iconRef, isRoot) => {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'move';
        
        if (!isRoot) {
            setIsHovering(true);

            const hoveredElement = document.elementFromPoint(e.clientX, e.clientY);
            if (iconRef?.current && (iconRef.current === hoveredElement || iconRef.current.contains(hoveredElement))) {
                setIsHoveringOverIcon(true);
            } else {
                setIsHoveringOverIcon(false);
            }
        }
    }, []);

    const handleDragLeave = useCallback((e, iconRef) => {
        e.preventDefault();
        e.stopPropagation();

        const relatedTarget = e.relatedTarget;
        if (!e.currentTarget.contains(relatedTarget)) {
            setIsHovering(false);
            setIsHoveringOverIcon(false);
        } else if (iconRef?.current && !iconRef.current.contains(relatedTarget)) {
            setIsHoveringOverIcon(false);
        }
    }, []);

    const handleDrop = useCallback(async (e, itemId, isFolder, iconRef, localItemData, fetchChildren) => {
        e.preventDefault();
        e.stopPropagation();
        setIsHovering(false);
        setIsHoveringOverIcon(false);

        try {
            const payload = JSON.parse(e.dataTransfer.getData('text/plain'));
            const draggedItem = payload.items[0];

            // Check if drop is on the icon
            const droppedOnIcon = isDropOnElement(e, iconRef);

            if (droppedOnIcon) {
                await persistQwantum(itemId, "pa", draggedItem.data._id);
                return;
            }

            const sourceParentId = draggedItem.pa;

            try {
                // Make target a folder if needed
                if (!isFolder) {
                    await stackOps.makeItemFolder(itemId);
                }

                // Move the item
                await stackOps.moveItem(draggedItem.itemId, itemId, sourceParentId);

                // Refresh and notify
                if (fetchChildren) {
                    await fetchChildren();
                }
                
                refreshAfterDrop(draggedItem.itemId, itemId, sourceParentId);

                if (debug) {
                    console.log(`Successfully moved "${draggedItem.text}" to "${itemId}"`);
                }

            } catch (error) {
                console.error("Drop operation failed:", error);
                alert(error.message);
            }

        } catch (error) {
            console.error("Error handling drop:", error);
        }
    }, [stackOps, refreshAfterDrop, debug]);

    // Grid-specific drop handler
    const handleGridDrop = useCallback(async (e, itemId, fetchChildren) => {
        e.preventDefault();
        e.stopPropagation();
        setIsHovering(false);

        try {
            const payload = JSON.parse(e.dataTransfer.getData('text/plain'));
            const draggedItem = payload.items[0];
            const sourceParentId = draggedItem.pa;

            try {
                await stackOps.moveItem(draggedItem.itemId, itemId, sourceParentId);

                if (fetchChildren) {
                    await fetchChildren();
                }

                refreshAfterDrop(draggedItem.itemId, itemId, sourceParentId);

                if (debug) {
                    console.log(`Successfully moved "${draggedItem.text}" to grid container`);
                }

            } catch (error) {
                console.error("Grid drop failed:", error);
                alert(error.message);
            }

        } catch (error) {
            console.error("Error handling grid drop:", error);
        }
    }, [stackOps, refreshAfterDrop, debug]);

    return {
        isHovering,
        isHoveringOverIcon,
        handleDragStart,
        handleDragEnd,
        handleDragEnter,
        handleDragOver,
        handleDragLeave,
        handleDrop,
        handleGridDrop
    };
}